import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { CopyToClipboard } from '@openx/components-core/lib/CopyToClipboard/CopyToClipboard';
import { theme } from '@openx/components-core/lib/theme/theme';
import { DealForecastStateMode } from '@openx/types/dealForecasting';
import { timeoutError } from '@openx/utils/state/deals/dealForecast/actions';
import type { JSX } from 'react';
import { useDealContext } from '../../../utils/context/DealContext';
import { forecastStateData } from './../constants';

export type DealForecastError =
  | typeof timeoutError
  | {
      response?: {
        headers?: Record<string, string>;
      };
    };

export type DealForecastStateProps = {
  mode: DealForecastStateMode;
  error?: DealForecastError;
};

export const xOpenxTraceId = 'x-openx-trace-id';

const styles: { [key: string]: SxProps<Theme> } = {
  gridItem: {
    '&:last-of-type': {
      marginTop: theme => theme.spacing(3),
    },
    lineHeight: theme => theme.spacing(3),
  },
};

export const DealForecastState = ({ mode, error }: DealForecastStateProps): JSX.Element => {
  const { heading, IconComponent, subtitle } = forecastStateData[mode];
  const trace_id =
    mode === 'error' && error && typeof error === 'object' && 'response' in error
      ? error.response?.headers?.[xOpenxTraceId]
      : undefined;
  const isTimeoutError = error === timeoutError;
  const allSetMode = mode === DealForecastStateMode.All_Set;
  const { isOa } = useDealContext();

  return (
    <Grid
      wrap="nowrap"
      container
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Grid sx={styles.gridItem}>
        <Typography
          data-test="forecast-heading"
          variant="h3"
          sx={{
            textAlign: 'center',
          }}
        >
          {heading(isTimeoutError)}
        </Typography>
      </Grid>

      <Grid sx={styles.gridItem}>
        <Typography
          data-test="forecast-subtitle"
          variant="body1"
          sx={{
            textAlign: 'center',
          }}
        >
          {subtitle(!!isOa)}
        </Typography>
      </Grid>

      {IconComponent && (
        <Grid sx={styles.gridItem}>
          <IconComponent
            data-test="forecast-state-icon"
            size={32}
            fill={allSetMode ? theme.palette.info.main : theme.palette.error.main}
          />
        </Grid>
      )}

      {mode === 'error' && trace_id && (
        <Typography
          data-test="forecast-error-trace-id"
          variant="body2"
          sx={{
            paddingTop: '16px',
          }}
        >
          {forecastStateData.error.traceIdText(trace_id)} <CopyToClipboard text={trace_id} />
        </Typography>
      )}
    </Grid>
  );
};
